import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";
import { ActionPayloadValidationError, requireStringArrayField, requireStringField } from "@slot/client-engine";

/** The game-defined action type: a VS battle wild expanding its reel. */
export const EXPANDING_WILD_ACTION_TYPE_NAME = "expanding_wild";

/** One battle as the server sends it: the reel that turns wild, its two values (top first) and the winner's index. */
export interface VsBattleInput {
  positionColumn: number;
  wildSymbolIdentifier: string;
  /** Display strings, `[top, bottom]`: whole numbers, the bottom strictly greater. */
  multipliers: readonly [string, string];
  winningIndex: 0 | 1;
}

/** The slice of the game presentation the handler drives (the VS battle feature). */
export interface VsBattlePresentationTarget {
  presentBattle(input: VsBattleInput): Promise<void>;
}

/**
 * GAME-DEFINED action: `expanding_wild`, this game's VS battle. The server sends one per reel whose VS a winning line
 * crosses, left to right, after the reveal and before the spin's line wins: the reel is wild and the battle's winner
 * multiplies the lines crossing it (the wins already count it; the client computes nothing). A VS no winning line
 * crosses sends no action and stays on the grid. The type left the canonical set (platform 804fba1), so the backend
 * and the fixtures carry `actionPhaseName: "win"` (an unknown type would resolve to "other", which super turbo skips).
 *
 * Every field is checked before the forward, so a refused payload draws nothing: the column a non-negative integer,
 * the two values whole-number strings with the bottom strictly greater (the backend's ordered pair, ADR 0047), the
 * winner's index 0 or 1. Each battle is awaited, so several in one spin play one after another.
 */
export class ExpandingWildActionHandler implements RoundActionHandler {
  public readonly actionTypeName = EXPANDING_WILD_ACTION_TYPE_NAME;

  public constructor(private readonly target: VsBattlePresentationTarget) {}

  public async handleAction(roundAction: RoundAction, _context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const columnText = requireStringField(this.actionTypeName, payload, "positionColumn");
    const wildSymbolIdentifier = requireStringField(this.actionTypeName, payload, "wildSymbolIdentifier");
    const positionColumn = Number.parseInt(columnText, 10);
    if (!/^\d+$/.test(columnText) || !Number.isInteger(positionColumn)) this.refuse("positionColumn", `Must be a non-negative integer string, got "${columnText}".`);

    const multipliers = requireStringArrayField(this.actionTypeName, payload, "multipliers");
    if (multipliers.length !== 2 || multipliers.some((value) => !/^\d+$/.test(value))) {
      this.refuse("multipliers", `Must be two whole-number strings, got ${JSON.stringify(multipliers)}.`);
    }
    const [top, bottom] = multipliers as [string, string];
    if (BigInt(bottom) <= BigInt(top)) this.refuse("multipliers", `The bottom must be strictly greater than the top, got ${JSON.stringify(multipliers)}.`);

    const winningIndexText = requireStringField(this.actionTypeName, payload, "winningIndex");
    if (winningIndexText !== "0" && winningIndexText !== "1") this.refuse("winningIndex", `Must be "0" or "1", got "${winningIndexText}".`);

    await this.target.presentBattle({ positionColumn, wildSymbolIdentifier, multipliers: [top, bottom], winningIndex: winningIndexText === "0" ? 0 : 1 });
  }

  private refuse(fieldName: string, message: string): never {
    throw new ActionPayloadValidationError(this.actionTypeName, fieldName, message);
  }
}
